import React, { useCallback, useEffect, useMemo } from 'react';
import { CalendarDays, Link2, Link2Off, RotateCcw, Search, SlidersHorizontal } from 'lucide-react';
import type { Edge, Node } from '../../core/models/scene';
import type { DiagramType } from '../../core/tools/interfaces/ITool';
import type {
  MultiViewEntityRef,
  MultiViewFilterClause,
  MultiViewLinkMode,
  MultiViewSharedQueryState,
} from '../../core/multi-view';
import { useDiagramInfo } from '../../hooks/useDiagramInfo';
import { useFolderData } from '../../hooks/useFolderData';
import { useCompassGraph } from '../../hooks/useCompassGraph';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useFolderRelationships } from '../data-binding/hooks/useFolderSourceBindings';
import type { FolderRecord } from '../data-binding/models/FolderRecord';
import type { PersistedNode } from '../compass/models/KnowledgeGraph';
import { useDiagramStore } from '../../stores/diagramStore';
import { useOrgChartViewStore } from '../../stores/orgchartViewStore';
import {
  useMultiCanvasViewStore,
} from '../../stores/multiCanvasViewStore';
import { OrgAllocationService } from '../orgchart/services/OrgAllocationService';
import type {
  OrgAllocationAssignment,
  OrgAllocationView,
} from '../orgchart/services/OrgAllocationService';
import { buildOrgAllocationDiagramScene } from '../orgchart/services/OrgAllocationDiagramSceneBuilder';
import { buildOrgAllocationDiffScene } from '../orgchart/services/OrgAllocationDiffSceneBuilder';
import {
  buildOrgAllocationGanttScene,
  type OrgAllocationGanttGranularity,
} from '../orgchart/services/OrgAllocationGanttSceneBuilder';
import { buildOrgAllocationPlanningScene } from '../orgchart/services/OrgAllocationPlanningSceneBuilder';
import { OrgAllocationSharedQueryService } from '../orgchart/services/OrgAllocationSharedQueryService';
import { OrgAllocationProjectionDetailService } from '../orgchart/services/OrgAllocationProjectionDetailService';
import { OrgAllocationProjectionJobService } from '../orgchart/services/OrgAllocationProjectionJobService';
import {
  resolveAtlasWorkloadPlanningPolicy,
  type AtlasWorkloadPlanningPolicy,
} from '../orgchart/services/AtlasWorkloadPlanningPolicy';
import { buildNetworkReviewItems } from '../network/services/NetworkReviewItemService';
import { buildNetworkReviewProjectionScene } from '../network/services/NetworkReviewProjectionSceneBuilder';
import { ProjectionSceneCanvas, type ProjectionViewpointOption } from '../../components/canvas/projections/ProjectionSceneCanvas';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

interface CanvasProjectionPaneHostProps {
  readonly diagramId: string | undefined;
  readonly diagramType?: DiagramType;
}

interface ProjectionSceneResult {
  readonly title: string;
  readonly subtitle?: string;
  readonly nodes: readonly Node[];
  readonly edges: readonly Edge[];
  readonly diagramType: DiagramType;
  readonly isLoading: boolean;
  readonly error: unknown;
  readonly emptyMessage: string;
  readonly telemetry?: ProjectionTelemetry;
}

interface ProjectionTelemetry {
  readonly sourceRecordCount?: number;
  readonly filteredRecordCount?: number;
  readonly renderedNodeCount?: number;
  readonly hiddenFactCount?: number;
  readonly draftChangeCount?: number;
  readonly bounded?: boolean;
}

interface AtlasTargetOption {
  readonly id: string;
  readonly label: string;
  readonly kind: string;
}

type AtlasProjectionGranularity = NonNullable<NonNullable<MultiViewSharedQueryState['time']>['granularity']>;

const ATLAS_RELATIONSHIP_TYPES = ['project_assignment', 'capacity_commitment'] as const;
const ATLAS_GANTT_VIEWPOINT = 'atlas-gantt-chart';
const ATLAS_CAPACITY_VIEWPOINT = 'atlas-capacity-planner';
const ATLAS_MAP_VIEWPOINT = 'atlas-workload-map';
const ATLAS_DIFF_VIEWPOINT = 'atlas-load-diff';
const BLUEPRINT_REVIEW_VIEWPOINT = 'blueprint-review-canvas';

const ATLAS_VIEWPOINTS: readonly ProjectionViewpointOption[] = [
  {
    id: ATLAS_GANTT_VIEWPOINT,
    label: 'Gantt chart',
    description: 'Show each person by week or month with load-height work bars.',
  },
  {
    id: ATLAS_CAPACITY_VIEWPOINT,
    label: 'Capacity planner',
    description: 'Plan from overloaded people, available people, and work demand lanes.',
  },
  {
    id: ATLAS_MAP_VIEWPOINT,
    label: 'Workload map',
    description: 'Show people-to-work relationships as diagram nodes and connectors.',
  },
  {
    id: ATLAS_DIFF_VIEWPOINT,
    label: 'Load diff',
    description: 'Compare the current workload window with the nearest source change.',
  },
];

const BLUEPRINT_VIEWPOINTS: readonly ProjectionViewpointOption[] = [
  {
    id: BLUEPRINT_REVIEW_VIEWPOINT,
    label: 'Review canvas',
    description: 'Show Blueprint resources and review signals as a canvas projection.',
  },
];

export const RegisteredCanvasProjectionPane: React.FC<CanvasProjectionPaneHostProps> = ({
  diagramId,
  diagramType,
}) => {
  const activeProjection = useMultiCanvasViewStore((state) => state.activeProjection);
  const linkMode = useMultiCanvasViewStore((state) => state.linkMode);
  const sharedQuery = useMultiCanvasViewStore((state) => state.sharedQuery);
  const projectionSelection = useMultiCanvasViewStore((state) => state.selection);
  const closeProjection = useMultiCanvasViewStore((state) => state.closeProjection);
  const setProjectionViewpoint = useMultiCanvasViewStore((state) => state.setProjectionViewpoint);
  const setLinkMode = useMultiCanvasViewStore((state) => state.setLinkMode);
  const updateSharedQuery = useMultiCanvasViewStore((state) => state.updateSharedQuery);
  const setSharedFilters = useMultiCanvasViewStore((state) => state.setSharedFilters);
  const clearSharedQuery = useMultiCanvasViewStore((state) => state.clearSharedQuery);
  const setProjectionSelection = useMultiCanvasViewStore((state) => state.setSelection);
  const primaryNodes = useDiagramStore((state) => state.nodes);
  const primarySelectedIds = useDiagramStore((state) => state.selectedIds);
  const setPrimarySelected = useDiagramStore((state) => state.setSelected);
  const diagramInfoQuery = useDiagramInfo(diagramId);
  const { entitlements } = useEntitlements();
  const { flags } = useFeatureFlags();
  const folderId = diagramInfoQuery.data?.folderId;
  const atlasEnabled = activeProjection?.kind === 'atlas-workload' && diagramType === 'org';
  const blueprintEnabled = activeProjection?.kind === 'blueprint-review' && diagramType === 'network';
  const asOfDate = useOrgChartViewStore((state) => state.asOfDate);
  const viewpointOptions = resolveViewpointOptions(activeProjection?.kind);
  const activeViewpointId = resolveActiveViewpointId(
    activeProjection?.viewpointId,
    viewpointOptions,
  );
  const allocationService = useMemo(() => new OrgAllocationService(), []);
  const queryService = useMemo(() => new OrgAllocationSharedQueryService(allocationService), [allocationService]);
  const detailService = useMemo(() => new OrgAllocationProjectionDetailService(), []);
  const projectionJobService = useMemo(() => new OrgAllocationProjectionJobService(allocationService), [allocationService]);
  const atlasPolicy = useMemo(
    () => resolveAtlasWorkloadPlanningPolicy(entitlements, flags),
    [entitlements, flags],
  );

  const relationshipsQuery = useFolderRelationships(folderId, {
    relationshipTypes: ATLAS_RELATIONSHIP_TYPES,
    limit: 1000,
    resolveIdentities: true,
    enabled: atlasEnabled,
  });
  const relationshipRecords = useMemo(
    () => relationshipsQuery.data?.records ?? [],
    [relationshipsQuery.data?.records],
  );
  const atlasTargets = useMemo(
    () => buildAtlasTargetOptions(relationshipRecords, allocationService),
    [allocationService, relationshipRecords],
  );

  const folderDataQuery = useFolderData(blueprintEnabled ? folderId : undefined);
  const orgId = folderDataQuery.data?.folder.orgId;
  const compassGraphQuery = useCompassGraph(
    blueprintEnabled ? folderId : undefined,
    blueprintEnabled ? orgId : undefined,
  );

  const scene = useMemo<ProjectionSceneResult>(() => {
    const kind = activeProjection?.kind;
    if (kind === 'atlas-workload') {
      return buildAtlasWorkloadScene({
        relationshipRecords: relationshipsQuery.data?.records ?? [],
        asOfDate,
        sharedQuery,
        allocationService,
        queryService,
        projectionJobService,
        policy: atlasPolicy,
        viewpointId: activeViewpointId ?? ATLAS_GANTT_VIEWPOINT,
        isLoading: diagramInfoQuery.isLoading || relationshipsQuery.isLoading,
        error: diagramInfoQuery.error ?? relationshipsQuery.error,
      });
    }
    if (kind === 'blueprint-review') {
      return buildBlueprintReviewScene({
        resources: compassGraphQuery.data?.nodes ?? [],
        isLoading: diagramInfoQuery.isLoading || folderDataQuery.isLoading || compassGraphQuery.isLoading,
        error: diagramInfoQuery.error ?? folderDataQuery.error ?? compassGraphQuery.error,
      });
    }
    return {
      title: 'Projection',
      nodes: [],
      edges: [],
      diagramType: 'generic',
      isLoading: false,
      error: null,
      emptyMessage: 'Choose a projection to open a second canvas.',
    };
  }, [
    activeProjection?.kind,
    activeViewpointId,
    atlasPolicy,
    asOfDate,
    allocationService,
    compassGraphQuery.data?.nodes,
    compassGraphQuery.error,
    compassGraphQuery.isLoading,
    diagramInfoQuery.error,
    diagramInfoQuery.isLoading,
    folderDataQuery.error,
    folderDataQuery.isLoading,
    projectionJobService,
    queryService,
    relationshipsQuery.data?.records,
    relationshipsQuery.error,
    relationshipsQuery.isLoading,
    sharedQuery,
  ]);

  useEffect(() => {
    if (!atlasEnabled || linkMode === 'independent') return;
    if (primarySelectedIds.size !== 1) return;
    const selectedId = Array.from(primarySelectedIds)[0];
    const selectedNode = primaryNodes.find((node) => node.id === selectedId);
    const ref = selectedNode ? entityRefForOrgPersonNode(selectedNode, 'atlas.org-tree') : null;
    if (!ref) return;
    if (
      projectionSelection?.entityId === ref.id &&
      projectionSelection.entityKind === ref.kind &&
      projectionSelection.sourcePaneId === ref.sourcePaneId
    ) {
      return;
    }
    setProjectionSelection({
      entityId: ref.id,
      entityKind: ref.kind,
      entityDomain: ref.domain,
      entityLabel: ref.label,
      sourcePaneId: ref.sourcePaneId,
      syncEnabled: true,
      resolved: true,
    });
  }, [
    atlasEnabled,
    linkMode,
    primaryNodes,
    primarySelectedIds,
    projectionSelection?.entityId,
    projectionSelection?.entityKind,
    projectionSelection?.sourcePaneId,
    setProjectionSelection,
  ]);

  const selectedEntityRef = useMemo<MultiViewEntityRef | null>(() => {
    if (!projectionSelection?.entityId) return null;
    return {
      id: projectionSelection.entityId,
      kind: projectionSelection.entityKind ?? undefined,
      domain: projectionSelection.entityDomain ?? undefined,
      label: projectionSelection.entityLabel ?? undefined,
      sourcePaneId: projectionSelection.sourcePaneId ?? undefined,
      resolved: projectionSelection.resolved ?? true,
    };
  }, [projectionSelection]);

  const handleSelectProjectionEntity = useCallback((entity: MultiViewEntityRef | null) => {
    if (!entity) {
      setProjectionSelection(null);
      return;
    }

    const nodeIds = atlasEnabled && linkMode !== 'independent'
      ? resolvePrimaryNodeIdsForEntity(entity, primaryNodes, relationshipRecords, allocationService)
      : [];
    const resolved = !atlasEnabled || linkMode === 'independent' || nodeIds.length > 0;
    setProjectionSelection({
      entityId: entity.id,
      entityKind: entity.kind ?? null,
      entityDomain: entity.domain ?? null,
      entityLabel: entity.label ?? null,
      sourcePaneId: entity.sourcePaneId ?? 'projection',
      syncEnabled: linkMode !== 'independent',
      resolved,
    });
    if (nodeIds.length > 0) setPrimarySelected(new Set(nodeIds));
  }, [
    allocationService,
    atlasEnabled,
    linkMode,
    primaryNodes,
    relationshipRecords,
    setPrimarySelected,
    setProjectionSelection,
  ]);

  if (!activeProjection) return null;
  const controlsSlot = atlasEnabled ? (
    <AtlasProjectionControls
      sharedQuery={sharedQuery}
      linkMode={linkMode}
      targetOptions={atlasTargets}
      policy={atlasPolicy}
      onSearchChange={(search) => updateSharedQuery({ search })}
      onTimeChange={(time) => updateSharedQuery({ time })}
      onTargetChange={(target) => updateSharedQuery({ target })}
      onFiltersChange={setSharedFilters}
      onClearQuery={clearSharedQuery}
      onLinkModeChange={setLinkMode}
    />
  ) : null;

  return (
    <ProjectionSceneCanvas
      key={`${activeProjection.kind}-${activeProjection.openedAt}`}
      title={scene.title}
      subtitle={scene.subtitle}
      controlsSlot={controlsSlot}
      viewpoints={viewpointOptions}
      activeViewpointId={activeViewpointId}
      onViewpointChange={setProjectionViewpoint}
      nodes={scene.nodes}
      edges={scene.edges}
      diagramType={scene.diagramType}
      linkMode={linkMode}
      selectedEntity={selectedEntityRef}
      resolveEntityRef={(node) => detailService.entityRefForNode(node)}
      resolveEntityDetail={(node) => detailService.detailForNode(node)}
      onSelectEntity={handleSelectProjectionEntity}
      isLoading={scene.isLoading}
      error={scene.error}
      emptyMessage={scene.emptyMessage}
      onClose={closeProjection}
    />
  );
};

function buildAtlasWorkloadScene(input: {
  readonly relationshipRecords: readonly FolderRecord[];
  readonly asOfDate: string | null;
  readonly sharedQuery: MultiViewSharedQueryState;
  readonly allocationService: OrgAllocationService;
  readonly queryService: OrgAllocationSharedQueryService;
  readonly projectionJobService: OrgAllocationProjectionJobService;
  readonly policy: AtlasWorkloadPlanningPolicy;
  readonly viewpointId: string;
  readonly isLoading: boolean;
  readonly error: unknown;
}): ProjectionSceneResult {
  if (!input.policy.canOpenSurface) {
    return {
      title: 'Atlas workload',
      nodes: [],
      edges: [],
      diagramType: 'org',
      isLoading: input.isLoading,
      error: input.error,
      emptyMessage: input.policy.reason ?? 'Atlas workload planning is not available for this workspace.',
    };
  }
  const viewOptions = allocationViewOptionsFromQuery(input.asOfDate, input.sharedQuery);
  const baseFilteredRecords = input.queryService.filterRelationshipRecords(
    input.relationshipRecords,
    input.sharedQuery,
  );
  const filteredRecords = baseFilteredRecords;
  const projectionSnapshot = input.projectionJobService.buildSnapshot(filteredRecords, {
    ...viewOptions,
    maxPeople: 25,
    maxTargets: 25,
  });
  const assignments = filteredRecords
    .map((record) => input.allocationService.relationshipRecordToAssignment(record))
    .filter((assignment): assignment is OrgAllocationAssignment => assignment !== null);
  let view = input.allocationService.buildAllocationView(filteredRecords, viewOptions);
  let inferredAsOf: Date | null = null;
  if (view.assignments.length === 0 && !input.asOfDate) {
    inferredAsOf = input.allocationService.suggestAllocationAsOf(filteredRecords);
    if (inferredAsOf) {
      view = input.allocationService.buildAllocationView(filteredRecords, {
        ...viewOptions,
        asOf: inferredAsOf,
      });
    }
  }
  if (input.viewpointId === ATLAS_MAP_VIEWPOINT) {
    return buildAtlasWorkloadMapScene(view, input, inferredAsOf, filteredRecords.length);
  }
  if (input.viewpointId === ATLAS_DIFF_VIEWPOINT) {
    return buildAtlasLoadDiffScene(view, {
      ...input,
      relationshipRecords: filteredRecords,
    }, input.allocationService, inferredAsOf);
  }
  if (input.viewpointId === ATLAS_GANTT_VIEWPOINT) {
    return buildAtlasGanttScene(assignments, input, filteredRecords.length, projectionSnapshot.summary.assignmentCount);
  }
  return buildAtlasCapacityPlannerScene(view, input, inferredAsOf, filteredRecords.length);
}

function buildAtlasGanttScene(
  assignments: readonly OrgAllocationAssignment[],
  input: {
    readonly asOfDate: string | null;
    readonly sharedQuery: MultiViewSharedQueryState;
    readonly isLoading: boolean;
    readonly error: unknown;
  },
  filteredRecordCount: number,
  materializedAssignmentCount: number,
): ProjectionSceneResult {
  const granularity = ganttGranularityFromQuery(input.sharedQuery);
  const hasAggregateDrilldown = input.sharedQuery.filters?.some((filter) =>
    filter.id === 'atlas-aggregate-drilldown',
  ) ?? false;
  const scene = buildOrgAllocationGanttScene(assignments, {
    idPrefix: ATLAS_GANTT_VIEWPOINT,
    asOf: input.sharedQuery.time?.asOf ?? input.asOfDate ?? input.sharedQuery.time?.windowStart ?? undefined,
    granularity,
    windowStart: input.sharedQuery.time?.windowStart,
    windowEnd: input.sharedQuery.time?.windowEnd,
    rowGrouping: hasAggregateDrilldown ? 'person' : 'auto',
    maxPeople: 12,
    maxPeriods: granularity === 'quarter' ? 8 : 12,
  });
  const hiddenParts = [
    scene.hiddenPeopleCount > 0 ? `${scene.hiddenPeopleCount} more people` : null,
    scene.hiddenPeriodCount > 0 ? `${scene.hiddenPeriodCount} more periods` : null,
    scene.hiddenAssignmentCount > 0 ? `${scene.hiddenAssignmentCount} more commitments` : null,
  ].filter(Boolean).join(' hidden, ');

  return {
    title: 'Atlas workload Gantt',
    subtitle: [
      granularityLabel(scene.granularity),
      scene.rowGrouping !== 'person' ? `Grouped by ${scene.aggregateField ?? scene.rowGrouping}` : null,
      formatPeriodRange(scene.periodLabels),
      hiddenParts || null,
    ].filter(Boolean).join(' - '),
    nodes: scene.nodes,
    edges: scene.edges,
    diagramType: 'org',
    isLoading: input.isLoading,
    error: input.error,
    emptyMessage: 'No dated workload relationships found. Add an allocation or capacity source to this Atlas folder.',
    telemetry: {
      filteredRecordCount,
      sourceRecordCount: materializedAssignmentCount,
      renderedNodeCount: scene.nodes.length,
      hiddenFactCount: scene.hiddenAssignmentCount,
      bounded: scene.scaleSummary.bounded,
    },
  };
}

function buildAtlasCapacityPlannerScene(
  view: OrgAllocationView,
  input: {
    readonly isLoading: boolean;
    readonly error: unknown;
  },
  inferredAsOf: Date | null,
  filteredRecordCount: number,
): ProjectionSceneResult {
  const scene = buildOrgAllocationPlanningScene(view, {
    idPrefix: ATLAS_CAPACITY_VIEWPOINT,
    maxAttentionPeople: 4,
    maxAvailablePeople: 5,
    maxTargets: 7,
  });
  const hiddenParts = [
    scene.hiddenAttentionPeopleCount > 0 ? `${scene.hiddenAttentionPeopleCount} more attention items` : null,
    scene.hiddenAvailablePeopleCount > 0 ? `${scene.hiddenAvailablePeopleCount} more available people` : null,
    scene.hiddenTargetCount > 0 ? `${scene.hiddenTargetCount} more work targets` : null,
  ].filter(Boolean).join(' hidden, ');

  return {
    title: 'Atlas capacity planner',
    subtitle: [
      asOfSubtitle(view.asOf),
      hiddenParts || null,
    ].filter(Boolean).join(' - '),
    nodes: scene.nodes,
    edges: scene.edges,
    diagramType: 'org',
    isLoading: input.isLoading,
    error: input.error,
    emptyMessage: inferredAsOf
      ? 'No workload relationships matched the current window.'
      : 'No workload relationships found. Add an allocation or capacity source to this Atlas folder.',
    telemetry: {
      filteredRecordCount,
      renderedNodeCount: scene.nodes.length,
      hiddenFactCount: scene.hiddenAttentionPeopleCount + scene.hiddenAvailablePeopleCount + scene.hiddenTargetCount,
      bounded: scene.hiddenAttentionPeopleCount > 0 ||
        scene.hiddenAvailablePeopleCount > 0 ||
        scene.hiddenTargetCount > 0,
    },
  };
}

function buildAtlasWorkloadMapScene(
  view: OrgAllocationView,
  input: {
    readonly isLoading: boolean;
    readonly error: unknown;
  },
  inferredAsOf: Date | null,
  filteredRecordCount: number,
): ProjectionSceneResult {
  const scene = buildOrgAllocationDiagramScene(view, {
    idPrefix: ATLAS_MAP_VIEWPOINT,
    maxPeople: 7,
    maxTargets: 7,
  });
  const hiddenParts = [
    scene.hiddenPeopleCount > 0 ? `${scene.hiddenPeopleCount} more people` : null,
    scene.hiddenTargetCount > 0 ? `${scene.hiddenTargetCount} more work targets` : null,
    scene.hiddenAssignmentCount > 0 ? `${scene.hiddenAssignmentCount} more links` : null,
  ].filter(Boolean).join(' hidden, ');

  return {
    title: 'Atlas workload map',
    subtitle: [
      asOfSubtitle(view.asOf),
      hiddenParts || null,
    ].filter(Boolean).join(' - '),
    nodes: scene.nodes,
    edges: scene.edges,
    diagramType: 'org',
    isLoading: input.isLoading,
    error: input.error,
    emptyMessage: inferredAsOf
      ? 'No workload relationships matched the current window.'
      : 'No workload relationships found. Add an allocation or capacity source to this Atlas folder.',
    telemetry: {
      filteredRecordCount,
      renderedNodeCount: scene.nodes.length,
      hiddenFactCount: scene.hiddenAssignmentCount,
      bounded: scene.hiddenPeopleCount > 0 ||
        scene.hiddenTargetCount > 0 ||
        scene.hiddenAssignmentCount > 0,
    },
  };
}

function buildAtlasLoadDiffScene(
  view: OrgAllocationView,
  input: {
    readonly relationshipRecords: readonly FolderRecord[];
    readonly isLoading: boolean;
    readonly error: unknown;
  },
  allocationService: OrgAllocationService,
  inferredAsOf: Date | null,
): ProjectionSceneResult {
  const comparisonDate = suggestAtlasComparisonDate(
    input.relationshipRecords,
    allocationService,
    view.asOf,
  );
  const comparisonView = comparisonDate
    ? allocationService.buildAllocationView(input.relationshipRecords, {
      asOf: comparisonDate,
    })
    : view;
  const scene = buildOrgAllocationDiffScene(view, comparisonView, {
    idPrefix: ATLAS_DIFF_VIEWPOINT,
    maxIncreases: 4,
    maxDecreases: 4,
    maxTargets: 7,
  });
  const hiddenParts = [
    scene.hiddenIncreaseCount > 0 ? `${scene.hiddenIncreaseCount} more increases` : null,
    scene.hiddenDecreaseCount > 0 ? `${scene.hiddenDecreaseCount} more decreases` : null,
    scene.hiddenTargetCount > 0 ? `${scene.hiddenTargetCount} more work targets` : null,
  ].filter(Boolean).join(' hidden, ');
  const fromLabel = formatDateLabel(view.asOf);
  const toLabel = formatDateLabel(comparisonView.asOf);

  return {
    title: 'Atlas load diff',
    subtitle: [
      comparisonDate
        ? `${fromLabel ?? 'Current'} to ${toLabel ?? 'comparison'}`
        : `${fromLabel ?? 'Current'} to nearest change`,
      hiddenParts || null,
    ].filter(Boolean).join(' - '),
    nodes: scene.nodes,
    edges: scene.edges,
    diagramType: 'org',
    isLoading: input.isLoading,
    error: input.error,
    emptyMessage: inferredAsOf
      ? 'No workload changes matched the current comparison.'
      : 'No workload relationships found. Add dated allocation or capacity sources to this Atlas folder.',
  };
}

function buildBlueprintReviewScene(input: {
  readonly resources: readonly PersistedNode[];
  readonly isLoading: boolean;
  readonly error: unknown;
}): ProjectionSceneResult {
  const graph = { nodes: [...input.resources] };
  const reviewItems = buildNetworkReviewItems({
    graph,
    baseline: null,
    coverageGaps: [],
    recentRuns: [],
    recentChangeSets: [],
  });
  const scene = buildNetworkReviewProjectionScene(input.resources, reviewItems, {
    maxResources: 18,
    maxReviewItems: 10,
  });
  const hiddenParts = [
    scene.hiddenResourceCount > 0 ? `${scene.hiddenResourceCount} more resources` : null,
    scene.hiddenReviewItemCount > 0 ? `${scene.hiddenReviewItemCount} more review items` : null,
  ].filter(Boolean).join(' hidden, ');

  return {
    title: 'Blueprint review canvas',
    subtitle: [
      `${input.resources.length} resources`,
      `${reviewItems.length} review signals`,
      hiddenParts || null,
    ].filter(Boolean).join(' - '),
    nodes: scene.nodes,
    edges: scene.edges,
    diagramType: 'generic',
    isLoading: input.isLoading,
    error: input.error,
    emptyMessage: 'No Blueprint resources or review signals are available for this folder yet.',
  };
}

const AtlasProjectionControls: React.FC<{
  readonly sharedQuery: MultiViewSharedQueryState;
  readonly linkMode: MultiViewLinkMode;
  readonly targetOptions: readonly AtlasTargetOption[];
  readonly policy: AtlasWorkloadPlanningPolicy;
  readonly onSearchChange: (search: string) => void;
  readonly onTimeChange: (time: MultiViewSharedQueryState['time']) => void;
  readonly onTargetChange: (target: MultiViewEntityRef | undefined) => void;
  readonly onFiltersChange: (filters: readonly MultiViewFilterClause[]) => void;
  readonly onClearQuery: () => void;
  readonly onLinkModeChange: (mode: MultiViewLinkMode) => void;
}> = ({
  sharedQuery,
  linkMode,
  targetOptions,
  policy,
  onSearchChange,
  onTimeChange,
  onTargetChange,
  onFiltersChange,
  onClearQuery,
  onLinkModeChange,
}) => {
  const filters = sharedQuery.filters ?? [];
  const workloadFilter = filters.find((filter) => filter.id === 'atlas-workload-filter');
  const activeWorkloadFilter = workloadFilterMode(workloadFilter);
  const activeDrilldown = filters.find((filter) => filter.id === 'atlas-aggregate-drilldown');
  const selectedTarget = sharedQuery.target?.id
    ? targetOptions.find((target) => target.id === sharedQuery.target?.id)
    : undefined;
  const hasSearch = Boolean((sharedQuery.search ?? '').trim());
  const activeWorkloadLabel = workloadFilterLabel(activeWorkloadFilter);
  const activeControlCount = [
    hasSearch,
    Boolean(sharedQuery.time?.asOf),
    Boolean(selectedTarget),
    Boolean(activeWorkloadFilter),
    Boolean(activeDrilldown),
  ].filter(Boolean).length;

  const setWorkloadFilter = (value: string) => {
    const next = filters.filter((filter) => filter.id !== 'atlas-workload-filter');
    const clause = workloadFilterClause(value);
    onFiltersChange(clause ? [...next, clause] : next);
  };

  return (
    <div className="flex min-w-0 items-center gap-1 text-xs">
      <div className="flex min-w-0 items-center gap-1 overflow-hidden">
        {hasSearch ? (
          <AtlasToolbarChip label={`Search: ${sharedQuery.search}`} />
        ) : null}
        {selectedTarget ? (
          <AtlasToolbarChip label={selectedTarget.label} />
        ) : null}
        {activeWorkloadLabel ? (
          <AtlasToolbarChip label={activeWorkloadLabel} />
        ) : null}
        {activeDrilldown ? (
          <AtlasToolbarChip
            label={filterValueLabel(activeDrilldown.value)}
            onClear={() => onFiltersChange(filters.filter((filter) => filter.id !== 'atlas-aggregate-drilldown'))}
          />
        ) : null}
        {!policy.canViewAdvancedAnalytics ? (
          <AtlasToolbarChip label="Basic" tone="muted" />
        ) : null}
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title="Workload controls"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            <span className="sr-only">Workload controls</span>
            {activeControlCount > 0 ? (
              <span className="rounded bg-primary/10 px-1 text-[10px] font-semibold text-primary">
                {activeControlCount}
              </span>
            ) : null}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={8} className="w-[min(560px,calc(100vw-32px))] p-3">
          <div className="grid gap-3 text-xs">
            <div className="grid grid-cols-2 gap-2">
              <label className="col-span-2 flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2">
                <Search className="h-3.5 w-3.5 text-muted-foreground" />
                <input
                  value={sharedQuery.search ?? ''}
                  onChange={(event) => onSearchChange(event.target.value)}
                  placeholder="Search people or work"
                  className="min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
                />
              </label>
              <label className="flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2">
                <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="date"
                  value={dateInputValue(sharedQuery.time?.asOf)}
                  onChange={(event) => onTimeChange({
                    ...sharedQuery.time,
                    asOf: event.target.value || undefined,
                  })}
                  disabled={!policy.canUseTimeTravel}
                  className="min-w-0 flex-1 bg-transparent text-xs outline-none disabled:opacity-50"
                  title={policy.canUseTimeTravel ? 'As-of date' : 'Time travel requires Atlas planning access'}
                />
              </label>
              <select
                value={sharedQuery.time?.granularity ?? 'month'}
                onChange={(event) => onTimeChange({
                  ...sharedQuery.time,
                  granularity: event.target.value as AtlasProjectionGranularity,
                })}
                disabled={!policy.canUseTimeTravel}
                className="h-8 rounded-md border border-border bg-background px-2 text-xs outline-none disabled:opacity-50"
                title={policy.canUseTimeTravel ? 'Gantt granularity' : 'Time granularity requires Atlas planning access'}
              >
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="quarter">Quarter</option>
              </select>
              <select
                value={activeWorkloadFilter}
                onChange={(event) => setWorkloadFilter(event.target.value)}
                className="h-8 rounded-md border border-border bg-background px-2 text-xs outline-none"
                title="Shared workload filter"
              >
                <option value="">All workload</option>
                <option value="available">Available &gt;= 25%</option>
                <option value="overloaded">Overloaded</option>
                <option value="unknown">Unknown allocation</option>
                <option value="unresolved">Unresolved</option>
                <option value="low_confidence">Low confidence</option>
              </select>
              <select
                value={selectedTarget?.id ?? ''}
                onChange={(event) => {
                  const target = targetOptions.find((candidate) => candidate.id === event.target.value);
                  onTargetChange(target ? {
                    id: target.id,
                    kind: target.kind,
                    domain: 'atlas',
                    label: target.label,
                  } : undefined);
                }}
                className="h-8 min-w-0 rounded-md border border-border bg-background px-2 text-xs outline-none"
                title="Target for filtering and planning"
              >
                <option value="">Any target</option>
                {targetOptions.map((target) => (
                  <option key={target.id} value={target.id}>
                    {target.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
              <span className="text-[11px] text-muted-foreground">
                Workload changes are saved and applied from Organization planning.
              </span>
              <button
                type="button"
                onClick={() => onLinkModeChange(linkMode === 'independent' ? 'linked-selection' : 'independent')}
                className="ml-auto inline-flex h-8 items-center gap-1 rounded-md border border-border bg-background px-2 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                title={linkMode === 'independent' ? 'Enable linked selection' : 'Disable linked selection'}
              >
                {linkMode === 'independent'
                  ? <Link2Off className="h-3.5 w-3.5" />
                  : <Link2 className="h-3.5 w-3.5" />}
                {linkMode === 'independent' ? 'Independent' : 'Linked'}
              </button>
              <button
                type="button"
                onClick={onClearQuery}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-background px-2 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                title="Clear shared query"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const AtlasToolbarChip: React.FC<{
  readonly label: string;
  readonly tone?: 'default' | 'accent' | 'muted';
  readonly onClear?: () => void;
}> = ({ label, tone = 'default', onClear }) => {
  const className = [
    'inline-flex h-6 max-w-[150px] shrink items-center gap-1 rounded-md border px-2 text-[11px] font-medium',
    tone === 'accent'
      ? 'border-blue-200 bg-blue-50 text-blue-700'
      : tone === 'muted'
        ? 'border-border bg-muted/50 text-muted-foreground'
        : 'border-border bg-background text-muted-foreground',
    onClear ? 'hover:bg-muted hover:text-foreground' : '',
  ].join(' ');

  if (!onClear) {
    return (
      <span className={className} title={label}>
        <span className="truncate">{label}</span>
      </span>
    );
  }

  return (
    <button type="button" onClick={onClear} className={className} title={`Clear ${label}`}>
      <span className="truncate">{label}</span>
      <span aria-hidden="true" className="shrink-0 text-[10px]">x</span>
    </button>
  );
};

function workloadFilterMode(filter: MultiViewFilterClause | undefined): string {
  if (!filter) return '';
  if (filter.field === 'availableAllocation' && filter.operator === 'gte') return 'available';
  if (filter.field === 'totalAllocation' && filter.operator === 'gte') return 'overloaded';
  if (filter.field === 'hasUnspecifiedAllocation' && filter.operator === 'eq') return 'unknown';
  if (filter.field === 'sourceState' && filter.operator === 'eq' && filter.value === 'unresolved') {
    return 'unresolved';
  }
  if (filter.field === 'sourceState' && filter.operator === 'eq' && filter.value === 'low_confidence') {
    return 'low_confidence';
  }
  return '';
}

function workloadFilterLabel(value: string): string | null {
  switch (value) {
    case 'available':
      return 'Available';
    case 'overloaded':
      return 'Overloaded';
    case 'unknown':
      return 'Unknown allocation';
    case 'unresolved':
      return 'Unresolved';
    case 'low_confidence':
      return 'Low confidence';
    default:
      return null;
  }
}

function workloadFilterClause(value: string): MultiViewFilterClause | null {
  switch (value) {
    case 'available':
      return {
        id: 'atlas-workload-filter',
        field: 'availableAllocation',
        operator: 'gte',
        value: 0.25,
        source: 'computed',
      };
    case 'overloaded':
      return {
        id: 'atlas-workload-filter',
        field: 'totalAllocation',
        operator: 'gte',
        value: 1.0001,
        source: 'computed',
      };
    case 'unknown':
      return {
        id: 'atlas-workload-filter',
        field: 'hasUnspecifiedAllocation',
        operator: 'eq',
        value: true,
      };
    case 'unresolved':
      return {
        id: 'atlas-workload-filter',
        field: 'sourceState',
        operator: 'eq',
        value: 'unresolved',
        source: 'computed',
      };
    case 'low_confidence':
      return {
        id: 'atlas-workload-filter',
        field: 'sourceState',
        operator: 'eq',
        value: 'low_confidence',
        source: 'computed',
      };
    default:
      return null;
  }
}

function buildAtlasTargetOptions(
  records: readonly FolderRecord[],
  allocationService: OrgAllocationService,
): AtlasTargetOption[] {
  const byId = new Map<string, AtlasTargetOption>();
  for (const record of records) {
    const assignment = allocationService.relationshipRecordToAssignment(record);
    if (!assignment || byId.has(assignment.targetExternalId)) continue;
    byId.set(assignment.targetExternalId, {
      id: assignment.targetExternalId,
      label: assignment.targetLabel,
      kind: assignment.targetKind,
    });
  }
  return Array.from(byId.values()).sort((left, right) => left.label.localeCompare(right.label));
}

function allocationViewOptionsFromQuery(
  fallbackAsOf: string | null,
  sharedQuery: MultiViewSharedQueryState,
): { readonly asOf?: Date | string } {
  return {
    asOf: sharedQuery.time?.asOf ??
      sharedQuery.time?.windowStart ??
      fallbackAsOf ??
      undefined,
  };
}

function ganttGranularityFromQuery(
  sharedQuery: MultiViewSharedQueryState,
): OrgAllocationGanttGranularity | undefined {
  const value = sharedQuery.time?.granularity;
  return value === 'week' || value === 'month' || value === 'quarter' ? value : undefined;
}

function granularityLabel(value: OrgAllocationGanttGranularity): string {
  if (value === 'week') return 'Weekly';
  if (value === 'quarter') return 'Quarterly';
  return 'Monthly';
}

function filterValueLabel(value: unknown): string {
  const text = stringValue(value);
  return text ? `Drilldown: ${text}` : 'Drilldown';
}

function entityRefForOrgPersonNode(
  node: Node,
  sourcePaneId: string,
): MultiViewEntityRef | null {
  if (!isOrgPersonNode(node)) return null;
  const attrs = isRecord(node.attrs) ? node.attrs : {};
  const props = isRecord(node.props) ? node.props : {};
  const id = firstString(
    props.personId,
    attrs.personId,
    props.sourceExternalId,
    attrs.sourceExternalId,
    props.employeeNumber,
    attrs.employeeNumber,
    props.email,
    attrs.email,
    node.id,
  );
  if (!id) return null;
  return {
    id,
    kind: 'person',
    domain: 'atlas',
    label: firstString(props.name, attrs.name, node.name, id) ?? id,
    sourcePaneId,
    resolved: true,
  };
}

function resolvePrimaryNodeIdsForEntity(
  entity: MultiViewEntityRef,
  nodes: readonly Node[],
  relationshipRecords: readonly FolderRecord[],
  allocationService: OrgAllocationService,
): string[] {
  if (entity.kind === 'person') {
    return nodes
      .filter((node) => entityMatchesOrgPersonNode(entity.id, node))
      .map((node) => node.id);
  }

  const personIds = new Set<string>();
  for (const record of relationshipRecords) {
    const assignment = allocationService.relationshipRecordToAssignment(record);
    if (assignment?.targetExternalId === entity.id) {
      personIds.add(assignment.personExternalId);
    }
  }
  if (personIds.size === 0) return [];
  return nodes
    .filter((node) => Array.from(personIds).some((personId) => entityMatchesOrgPersonNode(personId, node)))
    .map((node) => node.id);
}

function entityMatchesOrgPersonNode(entityId: string, node: Node): boolean {
  if (!isOrgPersonNode(node)) return false;
  const attrs = isRecord(node.attrs) ? node.attrs : {};
  const props = isRecord(node.props) ? node.props : {};
  return [
    node.id,
    props.personId,
    attrs.personId,
    props.sourceExternalId,
    attrs.sourceExternalId,
    props.employeeNumber,
    attrs.employeeNumber,
    props.email,
    attrs.email,
  ].some((value) => identityTokensMatch(entityId, value));
}

function isOrgPersonNode(node: Node): boolean {
  return String(node.type ?? '').startsWith('org_person') ||
    String((node as Node & { category?: unknown }).category ?? '') === 'orgchart';
}

function identityTokensMatch(left: unknown, right: unknown): boolean {
  const leftText = stringValue(left);
  const rightText = stringValue(right);
  if (!leftText || !rightText) return false;
  if (leftText === rightText) return true;
  return safeIdentityToken(leftText) === safeIdentityToken(rightText);
}

function safeIdentityToken(value: string): string {
  return value
    .toLowerCase()
    .replace(/^person:/, '')
    .replace(/[^a-z0-9]+/g, '');
}

function dateInputValue(value: string | undefined): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);
  return parsed.toISOString().slice(0, 10);
}

function formatDateLabel(value: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatPeriodRange(periodLabels: readonly string[]): string | null {
  const first = periodLabels[0];
  const last = periodLabels[periodLabels.length - 1];
  if (!first) return null;
  if (!last || first === last) return first;
  return `${first} to ${last}`;
}

function asOfSubtitle(value: string): string | null {
  const label = formatDateLabel(value);
  return label ? `As of ${label}` : null;
}

function resolveViewpointOptions(
  kind: string | undefined,
): readonly ProjectionViewpointOption[] {
  if (kind === 'atlas-workload') return ATLAS_VIEWPOINTS;
  if (kind === 'blueprint-review') return BLUEPRINT_VIEWPOINTS;
  return [];
}

function resolveActiveViewpointId(
  requested: string | undefined,
  options: readonly ProjectionViewpointOption[],
): string | undefined {
  if (requested && options.some((option) => option.id === requested)) return requested;
  return options[0]?.id;
}

function suggestAtlasComparisonDate(
  relationshipRecords: readonly FolderRecord[],
  allocationService: OrgAllocationService,
  baseIso: string,
): Date | null {
  const base = new Date(baseIso);
  if (Number.isNaN(base.getTime())) return null;
  const boundaries = relationshipRecords
    .map((record) => allocationService.relationshipRecordToAssignment(record))
    .flatMap((assignment) => {
      if (!assignment) return [];
      return [
        parseDateBoundary(assignment.validFrom),
        parseEndBoundary(assignment.validTo),
      ].filter((date): date is Date => !!date);
    })
    .filter((date) => Math.abs(date.getTime() - base.getTime()) > 1000)
    .sort((left, right) => left.getTime() - right.getTime());
  const uniqueBoundaries = uniqueDates(boundaries);
  const future = uniqueBoundaries.find((date) => date.getTime() > base.getTime());
  if (future) return future;
  return [...uniqueBoundaries].reverse().find((date) => date.getTime() < base.getTime()) ?? null;
}

function parseDateBoundary(value: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function parseEndBoundary(value: string | null): Date | null {
  const date = parseDateBoundary(value);
  if (!date) return null;
  const out = new Date(date);
  out.setUTCDate(out.getUTCDate() + 1);
  return out;
}

function uniqueDates(values: readonly Date[]): Date[] {
  const seen = new Set<number>();
  const out: Date[] = [];
  for (const value of values) {
    const time = value.getTime();
    if (seen.has(time)) continue;
    seen.add(time);
    out.push(value);
  }
  return out;
}

function firstString(...values: readonly unknown[]): string | null {
  for (const value of values) {
    const text = stringValue(value);
    if (text) return text;
  }
  return null;
}

function stringValue(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const text = String(value).trim();
  return text ? text : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
